import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineXS, LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import {
  FormSelect,
  GraphqlForm,
  TextField,
} from '#src/components/form/Form/index.tsx';
import { UploadFile } from '#src/components/form/UploadFile/index.tsx';
import type { GoogleReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { ReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { PRIVACY_POLICY } from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;

const PrivacyDisclaimer = styled.div`
  padding: var(--double-unit);
  background: var(--c-nd-50);
  border-radius: var(--unit);
`;

const NoWrapLink = styled(Link)`
  white-space: nowrap;
`;

const sportOptions = [
  { value: '-', label: '-' },
  { value: 'football', label: 'FOOTBALL' },
  { value: 'nba', label: 'NBA' },
  { value: 'mlb', label: 'MLB' },
];

const messages = defineMessages({
  submit: {
    id: 'NoCardEntryForm.Cta.submit',
    defaultMessage: 'Submit',
  },
});

type Props = {
  onSubmit: (
    attributes: any,
    onResult: (res: GraphQLResult) => void
  ) => Promise<void>;
  onSuccess: () => void;
  information?: ReactNode;
  submitMessage?: MessageDescriptor;
};
export const NoCardEntryForm = ({
  onSubmit,
  onSuccess,
  information,
  submitMessage,
}: Props) => {
  const { formatMessage } = useIntl();
  const [proofOfResidency, setProofOfResidency] = useState<File | null>(null);
  const [errors, setErrors] = useState<
    Record<string, MessageDescriptor> | undefined
  >(undefined);
  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);
  const { currentUser } = useCurrentUserContext();

  const basicValidation = ({
    email,
    username,
    sport,
  }: Record<string, string>) => {
    const errorMessages: Record<string, MessageDescriptor> = {};
    if (!currentUser) {
      errorMessages.currentUser = {
        id: 'NoCardEntryForm.error.currentUser',
        defaultMessage: 'You must be logged in to enter the contest',
      };
      return errorMessages;
    }
    if (!sport || sport === sportOptions[0].value) {
      errorMessages.sport = {
        id: 'NoCardEntryForm.error.sport',
        defaultMessage: 'You must select the sport',
      };
    }

    if (email !== currentUser.email) {
      errorMessages.email = {
        id: 'NoCardEntryForm.error.email',
        defaultMessage: 'The email must be the same as your Sorare account',
      };
    }
    if (username !== currentUser.nickname) {
      errorMessages.username = {
        id: 'NoCardEntryForm.error.username',
        defaultMessage: 'The username must be the same as your Sorare account',
      };
    }
    return errorMessages;
  };

  return (
    <GraphqlForm
      onSubmit={(attributes, onResult, onCancel) => {
        const errorMessages = basicValidation(attributes);
        if (Object.keys(errorMessages).length === 0) {
          recaptchaRef.current?.reset();
          recaptchaRef.current?.executeAsync().then(recaptchaTokenV2 => {
            onSubmit(
              { ...attributes, recaptchaTokenV2, proofOfResidency },
              onResult
            );
          });
        } else {
          onResult({
            errors: errorMessages
              ? Object.entries(errorMessages).map(
                  ([fieldError, errorMessage]) =>
                    ({
                      message: formatMessage(errorMessage),
                      code: fieldError,
                      path: ['', fieldError],
                    }) as any
                )
              : null,
          });
          setErrors(errorMessages);
          onCancel();
        }
      }}
      errorMessages={errors}
      onSuccess={onSuccess}
      render={(Error, SubmitButton) => (
        <Root>
          <PrivacyDisclaimer>
            <HeadlineXS as="h6">
              <FormattedMessage
                id="NoCardEntryForm.privacyDisclaimerTitle"
                defaultMessage="Privacy disclaimer"
              />
            </HeadlineXS>
            <LabelM>
              <FormattedMessage
                id="NoCardEntryForm.privacyDisclaimer"
                defaultMessage="Sorare processes the data collected to enable your participation in the current Gameweek and to combat fraud and attempted violations of applicable Terms and Conditions and Rules.
              To learn more about how we handle your personal data and to exercise your rights, see <link>Sorare's Privacy Policy</link>.
              "
                values={{
                  link: (children: ReactNode[]) => (
                    <NoWrapLink
                      target="_blank"
                      rel="noopener noreferrer"
                      to={PRIVACY_POLICY}
                    >
                      {children}
                    </NoWrapLink>
                  ),
                }}
              />
            </LabelM>
          </PrivacyDisclaimer>
          <TextField
            name="email"
            label={
              <FormattedMessage
                id="NoCardEntryForm.email"
                defaultMessage="Your email address"
              />
            }
            required
          />
          <TextField
            name="username"
            label={
              <FormattedMessage
                id="NoCardEntryForm.username"
                defaultMessage="Your Sorare username"
              />
            }
            required
          />
          <FormSelect
            id="sport"
            name="sport"
            label={formatMessage({
              id: 'NoCardEntryForm.sport',
              defaultMessage: 'Sport',
            })}
            options={sportOptions}
            required
          />
          <TextField
            name="proofOfResidencyName"
            label={
              <FormattedMessage
                id="NoCardEntryForm.proofOfResidency"
                defaultMessage="Proof of residence"
              />
            }
            value={proofOfResidency?.name}
            required
            endAdornment={
              <UploadFile
                name="proofOfResidency"
                currentFileUrl={proofOfResidency?.name || null}
                onChange={fileData => setProofOfResidency(fileData.file)}
                type="application/pdf"
                validExtensions={['.pdf']}
                buttonLabel={
                  <FormattedMessage
                    id="NoCardEntryForm.uploadFile"
                    defaultMessage="Upload a file (pdf, maximum size: 2Mb)"
                  />
                }
              />
            }
          />
          <Vertical gap={0.5}>
            <LabelS>
              <FormattedMessage
                id="NoCardEntryForm.proofOfResidency.disclaimer"
                defaultMessage="Document will be validated at a later stage, additional information may be required for suspicious applications."
              />
            </LabelS>
            <LabelS>
              <FormattedMessage
                id="NoCardEntryForm.proofOfResidency.details"
                defaultMessage={`Accepted documents (issued within the last 3 months):{br}
                               - Telephone bill (including cell phone bill){br}
                               - Electricity or gas bill{br}
                               - Rent receipt (from a social organization or real estate agency) or property title{br}
                               - Water bill{br}
                               - Certificate or invoice of insurance of the accommodation{br}`}
                values={{
                  br: <br />,
                }}
              />
            </LabelS>
          </Vertical>

          <ReCAPTCHA ref={recaptchaRef} />
          {information}
          <Vertical gap={0}>
            <Error code />
          </Vertical>
          <SubmitButton size="medium">
            <FormattedMessage {...(submitMessage || messages.submit)} />
          </SubmitButton>
          <LabelS className="text-right">
            <FormattedMessage
              id="NoCardEntryForm.requiredFields"
              defaultMessage="* mandatory fields"
            />
          </LabelS>
        </Root>
      )}
    />
  );
};
